import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { AppLayout } from "@/components/shell/AppLayout";
import { MissionControl } from "@/features/missionControl/MissionControl";
import { WorkshopDashboard } from "@/features/workshop/WorkshopDashboard";
import { HandshakePage } from "@/features/ws4-handshake/HandshakePage";
import { StressTestPage } from "@/features/ws4-stress-test/StressTestPage";
import { RepositoryOverlay } from "@/features/repository/RepositoryOverlay";
import { NotFound } from "@/pages/NotFound";
import { AuthCallbackPage } from "@/pages/AuthCallbackPage";
import { AuthProvider } from "@/hooks/use-auth";
import { ClientProvider } from "@/features/client/ClientProvider";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

function AppProviders({ children }: { children: React.ReactNode }) {
  // Boot hydration (storage migration + asset locker + captures) moved
  // into ClientProvider (multi-client plan): it must run AFTER the active
  // client resolves so IndexedDB reads observe the client-scoped keys.
  // With the flag off, ClientProvider resolves immediately at mount, so
  // the timing matches the old boot effect.
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        attribute="class"
        defaultTheme="light"
        enableSystem
        storageKey="xaes-theme"
      >
        <TooltipProvider delayDuration={200}>
          <Toaster />
          <Sonner position="top-right" richColors closeButton />
          <AuthProvider>
            <ClientProvider>{children}</ClientProvider>
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export function App() {
  return (
    <AppProviders>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <Routes>
          {/* OIDC callback lives outside AppLayout — its purpose is to
              complete the redirect flow before the AuthGate has any user
              state to act on. Once signinCallback() resolves, it navigates
              to "/" and the AppLayout gate sees the freshly-signed-in user. */}
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route element={<AppLayout />}>
            <Route path="/" element={<MissionControl />} />
            <Route path="/workshop/:id" element={<WorkshopDashboard />} />
            <Route path="/workshop/:id/handshake" element={<HandshakePage />} />
            <Route path="/workshop/:id/stress-test" element={<StressTestPage />} />
            {/* The Repository is a URL-driven overlay: the route mounts it
                open over an (empty) main, so deep-links + refresh + back all
                work, while ESC / CLOSE dismiss it back to the opener. */}
            <Route path="/repository" element={<RepositoryOverlay />} />
            {/* Former Documents route — folded into the unified Repository. */}
            <Route path="/documents" element={<Navigate to="/repository" replace />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AppProviders>
  );
}
